import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { WidgetProps } from './registry';
import { decodeWidgetData } from './decode-widget-data';

type Item = { prospectId: string; handle?: string; reason: string; phase?: string };

export function TodayQueue(props: WidgetProps) {
  const items = (decodeWidgetData(props.payload).items ?? []) as Item[];
  const [drafting, setDrafting] = useState<Record<string, 'pending' | 'done' | 'error'>>({});
  const navigate = useNavigate();

  // H.2.4: /outreach/prospects/:id retired — prospect links now scope the chat.
  const scopeToProspect = (prospectId: string) => {
    sessionStorage.setItem('outreach-scope', JSON.stringify({ prospectId }));
    navigate('/outreach');
  };

  const draft = async (prospectId: string) => {
    setDrafting((s) => ({ ...s, [prospectId]: 'pending' }));
    let res: Response | null = null;
    try {
      res = await fetch('/admin/outreach/draft', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prospectId, channel: 'x_dm' }),
      });
    } catch {
      // Network failure — leave retry-able
    }
    setDrafting((s) => ({ ...s, [prospectId]: res && res.ok ? 'done' : 'error' }));
  };

  if (items.length === 0) {
    return <p className="text-sm text-foreground-mute">Nothing on the queue today. 🎉</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={it.prospectId} className="flex items-center justify-between border-l-2 border-primary pl-3">
          <div className="text-sm">
            <button
              type="button"
              onClick={() => scopeToProspect(it.prospectId)}
              className="font-medium hover:underline text-left"
            >
              {it.handle ? `@${it.handle}` : it.prospectId}
            </button>
            <p className="text-xs text-foreground-mute">{it.reason}</p>
          </div>
          <div className="flex items-center gap-2">
            {it.phase && (
              <span className="text-[10px] uppercase tracking-wider text-foreground-mute">{it.phase}</span>
            )}
            <button
              type="button"
              onClick={() => draft(it.prospectId)}
              disabled={drafting[it.prospectId] === 'pending' || drafting[it.prospectId] === 'done'}
              className="rounded-md bg-primary px-2 py-0.5 text-xs text-primary-foreground disabled:opacity-40"
              data-testid={`today-draft-${it.prospectId}`}
            >
              {drafting[it.prospectId] === 'pending'
                ? 'Drafting…'
                : drafting[it.prospectId] === 'done'
                  ? 'Drafted ✓'
                  : 'Draft'}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
